// src/pages/Tenders.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { useGraphQL } from '../graphql';
import DataTable from '../components/DataTable';
import Modal, { ModalForm, ModalConfirm } from '../components/Modal';
import { StatusBadge } from '../components/Badge';
import ProcessingStateModal from '../components/ProcessingStateModal';
import StateFilterChips from '../components/StateFilterChips';
import ProcessingStateTimeline from '../components/ProcessingStateTimeline';
import { calculateStateCounts, getStateInfo, isProcessingState } from '../utils/processingState';
import {
    PlusIcon,
    EyeIcon,
    PencilIcon,
    PlayIcon,
    ChartBarIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    MagnifyingGlassIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Tenders = () => {
    const { executeQuery, executeMutation, queries, mutations, loading, error } = useGraphQL();
    const [tenders, setTenders] = useState([]);
    const [selectedTender, setSelectedTender] = useState(null);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCrawlModal, setShowCrawlModal] = useState(false);
    const [showAssessModal, setShowAssessModal] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState(false);
    const [showDeepCrawlModal, setShowDeepCrawlModal] = useState(false);
    const [showStateModal, setShowStateModal] = useState(false);
    const [filters, setFilters] = useState({ status: '', processingStates: [] });

    // Search query state
    const [searchQuery, setSearchQuery] = useState('');

    // Company context for tenant isolation
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        externalId: '',
        description: '',
        type: '',
        location: {
            address: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            nutsCode: '',
            latitude: null,
            longitude: null,
        },
        url: '',
        deadline: '',
        startDate: ''
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        loadTenders();
        loadCompanies();
    }, [filters, searchQuery]);

    const loadCompanies = async () => {
        try {
            const data = await executeQuery(queries.getCompanies(), { limit: 100, offset: 0 });
            setCompanies(data.adminCompanies || []);
            if (data.adminCompanies?.length > 0 && !selectedCompanyId) {
                setSelectedCompanyId(data.adminCompanies[0].id);
            }
        } catch (err) {
            console.error('Failed to load companies:', err);
        }
    };

    const loadTenders = async () => {
        try {
            // Use searchTenders when there's a search query, otherwise use getTenders
            if (searchQuery.trim()) {
                const searchInput = {
                    query: searchQuery.trim(),
                    statuses: filters.status ? [filters.status] : undefined,
                    processingStates: filters.processingStates.length > 0 ? filters.processingStates : undefined,
                    limit: 100
                };
                const data = await executeQuery(queries.searchTenders(), { input: searchInput });
                // searchTenders returns items with tender objects
                const tenderList = (data.searchTenders?.items || []).map(item => item.tender);
                setTenders(tenderList);
            } else {
                const filterVars = {
                    status: filters.status || undefined,
                    limit: 100,
                    offset: 0
                };
                const data = await executeQuery(queries.getTenders(), filterVars);
                setTenders(data.tenders || []);
            }
        } catch (err) {
            console.error('Failed to load tenders:', err);
        }
    };

    // Calculate state counts for filter chips
    const stateCounts = useMemo(() => calculateStateCounts(tenders), [tenders]);

    // Filter tenders by selected processing states (client-side)
    const filteredTenders = useMemo(() => {
        if (filters.processingStates.length === 0) return tenders;
        return tenders.filter(tender =>
            filters.processingStates.includes(tender.processingState || 'DRAFT')
        );
    }, [tenders, filters.processingStates]);

    // Handle action from ProcessingStateModal
    const handleStateAction = (actionKey, tender) => {
        setSelectedTender(tender);
        setShowStateModal(false);

        switch (actionKey) {
            case 'crawl':
                setShowCrawlModal(true);
                break;
            case 'deepCrawl':
                setShowDeepCrawlModal(true);
                break;
            case 'convert':
                setShowConvertModal(true);
                break;
            case 'assess':
                setShowAssessModal(true);
                break;
            default:
                console.warn('Unknown action:', actionKey);
        }
    };

    // Open the state modal for a tender
    const openStateModal = (tender) => {
        setSelectedTender(tender);
        setShowStateModal(true);
    };

    const loadTenderDetail = async (tenderId) => {
        try {
            // Load tender
            const tenderData = await executeQuery(queries.getTender(), { id: tenderId });
            setSelectedTender(tenderData.tender);

            // Load assessment separately
            try {
                const assessmentData = await executeQuery(queries.getAssessmentByTender(), { tenderId });
                setSelectedAssessment(assessmentData.tenderAssessment);
            } catch (err) {
                // Assessment might not exist yet
                console.warn('No assessment found for tender:', err);
                setSelectedAssessment(null);
            }
        } catch (err) {
            console.error('Failed to load tender details:', err);
        }
    };

    const handleCreateTender = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.createTender, {
                input: {
                    title: formData.title,
                    externalId: formData.externalId,
                    description: formData.description,
                    type: formData.type || undefined,
                    location: formData.location || undefined,
                    url: formData.url || undefined,
                    deadline: formData.deadline ? new Date(formData.deadline).toISOString() : undefined,
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined
                }
            });

            if (result.createTender.success) {
                setShowCreateModal(false);
                resetForm();
                await loadTenders();
            } else {
                alert('Error: ' + result.createTender.error);
            }
        } catch (err) {
            console.error('Failed to create tender:', err);
            alert('Failed to create tender: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleUpdateTender = async (e) => {
        e.preventDefault();
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.updateTender, {
                id: selectedTender.id,
                input: {
                    title: formData.title,
                    description: formData.description,
                    type: formData.type || undefined,
                    location: formData.location || undefined,
                    url: formData.url || undefined,
                    deadline: formData.deadline ? new Date(formData.deadline).toISOString() : undefined,
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined
                }
            });

            if (result.updateTender.success) {
                setShowEditModal(false);
                setSelectedTender(null);
                setSelectedAssessment(null);
                resetForm();
                await loadTenders();
            } else {
                alert('Error: ' + result.updateTender.error);
            }
        } catch (err) {
            console.error('Failed to update tender:', err);
            alert('Failed to update tender: ' + err.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCrawlTender = async () => {
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.adminCrawlTender, {
                tenderId: selectedTender.id
            });

            if (result.adminCrawlTender.success) {
                setShowCrawlModal(false);
                await loadTenders();
                alert('Crawl job started: ' + result.adminCrawlTender.jobId);
            } else {
                alert('Error: ' + result.adminCrawlTender.error);
            }
        } catch (err) {
            console.error('Failed to start crawl:', err);
            alert('Failed to start crawl: ' + err.message);
        } finally {
            setSubmitLoading(false);
            setSelectedTender(null);
            setSelectedAssessment(null);
        }
    };

    const handleAssessTender = async () => {
        if (!selectedCompanyId) {
            alert('Please select a company for assessment');
            return;
        }
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.adminAssessTender, {
                tenderId: selectedTender.id,
                companyId: selectedCompanyId
            });

            if (result.adminAssessTender.success) {
                setShowAssessModal(false);
                await loadTenders();
                alert('Assessment job started: ' + result.adminAssessTender.jobId);
            } else {
                alert('Error: ' + result.adminAssessTender.error);
            }
        } catch (err) {
            console.error('Failed to start assessment:', err);
            alert('Failed to start assessment: ' + err.message);
        } finally {
            setSubmitLoading(false);
            setSelectedTender(null);
            setSelectedAssessment(null);
        }
    };

    const handleConvertDocuments = async () => {
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.adminConvertTenderDocuments, {
                tenderId: selectedTender.id
            });

            if (result.adminConvertTenderDocuments.success) {
                setShowConvertModal(false);
                await loadTenders();
                alert('Document conversion job started: ' + result.adminConvertTenderDocuments.jobId);
            } else {
                alert('Error: ' + result.adminConvertTenderDocuments.error);
            }
        } catch (err) {
            console.error('Failed to start document conversion:', err);
            alert('Failed to start document conversion: ' + err.message);
        } finally {
            setSubmitLoading(false);
            setSelectedTender(null);
        }
    };

    const handleDeepCrawl = async () => {
        setSubmitLoading(true);
        try {
            const result = await executeMutation(mutations.adminDeepCrawlTender, {
                tenderId: selectedTender.id
            });

            if (result.adminDeepCrawlTender.success) {
                setShowDeepCrawlModal(false);
                await loadTenders();
                alert('Deep crawl job started: ' + result.adminDeepCrawlTender.jobId);
            } else {
                alert('Error: ' + result.adminDeepCrawlTender.error);
            }
        } catch (err) {
            console.error('Failed to start deep crawl:', err);
            alert('Failed to start deep crawl: ' + err.message);
        } finally {
            setSubmitLoading(false);
            setSelectedTender(null);
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            externalId: '',
            description: '',
            type: '',
            // schema.graphql changed to:
            // type LocationInfo {
            //     address: String
            //     city: String
            //     state: String
            //     postalCode: String
            //     country: String
            //     nutsCode: String
            //     latitude: Float
            //     longitude: Float
            // }
            location: {
                address: '',
                city: '',
                state: '',
                postalCode: '',
                country: '',
                nutsCode: '',
                latitude: null,
                longitude: null,
            },
            url: '',
            deadline: '',
            startDate: ''
        });
    };

    const openDetailModal = async (tender) => {
        await loadTenderDetail(tender.id);
        setShowDetailModal(true);
    };

    const openEditModal = (tender) => {
        setSelectedTender(tender);
        setFormData({
            title: tender.title,
            externalId: tender.externalId,
            description: tender.description || '',
            type: tender.type || '',
            location: tender.location || '',
            url: tender.url || '',
            deadline: tender.deadline ? tender.deadline.split('T')[0] : '',
            startDate: tender.startDate ? tender.startDate.split('T')[0] : ''
        });
        setShowEditModal(true);
    };

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
        setSelectedTender(null);
        setSelectedAssessment(null);
    };

    const columns = [
        {
            key: 'title',
            title: 'Tender',
            sortable: true,
            render: (title, tender) => (
                <div>
                    <div className="font-medium text-white truncate max-w-xs">{title}</div>
                    <div className="text-sm text-gray-400">ID: {tender.externalId}</div>
                    <div className="text-xs text-gray-500">{tender.type}</div>
                </div>
            )
        },
        {
            key: 'status',
            title: 'Status',
            sortable: true,
            render: (status) => <StatusBadge status={status} />
        },
        {
            key: 'processingState',
            title: 'Pipeline',
            render: (processingState, tender) => {
                const stateInfo = getStateInfo(processingState);
                const isInProgress = isProcessingState(processingState);
                const StateIcon = stateInfo.Icon;

                // Determine quick action based on state
                const getQuickAction = () => {
                    switch (processingState) {
                        case 'DISCOVERED':
                        case 'ERROR':
                            return (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTender(tender);
                                        setShowCrawlModal(true);
                                    }}
                                    className="p-1 rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-all duration-150"
                                    title="Start crawl"
                                >
                                    <PlayIcon className="h-4 w-4" />
                                </button>
                            );
                        case 'CRAWLED':
                            return (
                                <div className="flex space-x-1">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTender(tender);
                                            setShowConvertModal(true);
                                        }}
                                        className="p-1 rounded-lg text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-all duration-150"
                                        title="Convert documents"
                                    >
                                        <DocumentTextIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedTender(tender);
                                            setShowDeepCrawlModal(true);
                                        }}
                                        className="p-1 rounded-lg text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 transition-all duration-150"
                                        title="Deep crawl"
                                    >
                                        <MagnifyingGlassIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            );
                        case 'ENRICHED':
                            return (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTender(tender);
                                        setShowConvertModal(true);
                                    }}
                                    className="p-1 rounded-lg text-purple-600 hover:text-purple-700 hover:bg-purple-50 transition-all duration-150"
                                    title="Convert documents"
                                >
                                    <DocumentTextIcon className="h-4 w-4" />
                                </button>
                            );
                        case 'PROCESSED':
                            return (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedTender(tender);
                                        setShowAssessModal(true);
                                    }}
                                    className="p-1 rounded-lg text-green-600 hover:text-green-700 hover:bg-green-50 transition-all duration-150"
                                    title="Start assessment"
                                >
                                    <ChartBarIcon className="h-4 w-4" />
                                </button>
                            );
                        case 'ASSESSED':
                            return (
                                <CheckCircleIcon className="h-4 w-4 text-emerald-600" title="Complete" />
                            );
                        default:
                            if (isInProgress) {
                                return (
                                    <ArrowPathIcon className="h-4 w-4 text-yellow-500 animate-spin" title="Processing..." />
                                );
                            }
                            return null;
                    }
                };

                return (
                    <div className="flex items-center space-x-2">
                        {/* Clickable state badge that opens modal */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                openStateModal(tender);
                            }}
                            className={`
 flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium
 ${stateInfo.bgClass} ${stateInfo.textClass}
 hover:ring-2 ${stateInfo.ringClass}/50 transition-all duration-150 cursor-pointer
 ${isInProgress ? 'animate-pulse' : ''}
 `}
                            title="Click to view pipeline details"
                        >
                            <StateIcon className={`h-3.5 w-3.5 ${isInProgress ? 'animate-spin' : ''}`} />
                            {stateInfo.label}
                        </button>
                        {getQuickAction()}
                    </div>
                );
            }
        },
        {
            key: 'deadline',
            title: 'Deadline',
            sortable: true,
            render: (deadline) => {
                if (!deadline) return <span className="text-gray-400">-</span>;
                const date = new Date(deadline);
                const isExpired = date < new Date();
                return (
                    <div className={isExpired ? 'text-red-600' : 'text-white'}>
                        <div>{date.toLocaleDateString()}</div>
                        <div className="text-xs text-gray-400">{date.toLocaleTimeString()}</div>
                    </div>
                );
            }
        },
        {
            key: 'location',
            title: 'Location',
            render: (location) => {
                if (!location) return <span className="text-gray-400">-</span>;
                const parts = [];
                if (location.address) parts.push(location.address);
                if (location.city) parts.push(location.city);
                if (location.state) parts.push(location.state);
                if (location.postalCode) parts.push(location.postalCode);
                if (location.country) parts.push(location.country);
                if (location.nutsCode) parts.push(location.nutsCode);
                if (location.latitude) parts.push(`Lat: ${location.latitude}`);
                if (location.longitude) parts.push(`Lng: ${location.longitude}`);
                return <span className="text-white text-sm">{parts.join(', ')}</span>;
            }
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, tender) => (
                <div className="flex space-x-2">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(tender);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="View details"
                    >
                        <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(tender);
                        }}
                        className="p-1 text-gray-400 hover:text-primary"
                        title="Edit tender"
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTender(tender);
                            setShowCrawlModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-accent-blue"
                        title="Crawl tender"
                    >
                        <PlayIcon className="h-4 w-4" />
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTender(tender);
                            setShowAssessModal(true);
                        }}
                        className="p-1 text-gray-400 hover:text-accent-green"
                        title="Assess tender"
                    >
                        <ChartBarIcon className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    const filterOptions = [
        { value: '', label: 'All Tenders' },
        { value: 'NOT_VIEWED', label: 'Not Viewed' },
        { value: 'RELEVANT', label: 'Relevant' },
        { value: 'NOT_LUCRATIVE', label: 'Not Lucrative' },
        { value: 'DECLINED', label: 'Declined' },
        { value: 'SUBMITTED', label: 'Submitted' }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">Tenders</h1>
                    <p className="text-gray-400 mt-1">Manage and track tender opportunities</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Global Company Selector for Tenant Isolation */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-400 whitespace-nowrap">Company Context:</label>
                        <select
                            value={selectedCompanyId}
                            onChange={(e) => setSelectedCompanyId(e.target.value)}
                            className="bg-gray-700 border border-gray-600 text-white px-3 py-2 text-sm min-w-[200px]"
                        >
                            {companies.length === 0 && (
                                <option value="">Loading companies...</option>
                            )}
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>
                                    {company.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={() => setShowCreateModal(true)}
                        className="flex items-center px-4 py-2 rounded-lg bg-primary text-on-accent hover:brightness-110 transition-all duration-150"
                    >
                        <PlusIcon className="h-5 w-5 mr-2" />
                        New Tender
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="surface-card p-4 space-y-4">
                {/* Search and status filter */}
                <div className="flex items-center gap-4">
                    {/* Full-text search input */}
                    <div className="flex-1 relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search tenders by title or description..."
                            className="w-full bg-gray-700 border border-gray-600 text-white pl-10 pr-4 py-1.5 text-sm"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                            >
                                &times;
                            </button>
                        )}
                    </div>
                    <label className="text-sm font-medium text-gray-300 whitespace-nowrap">
                        Status:
                    </label>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        className="bg-gray-700 border border-gray-600 text-white px-3 py-1.5 text-sm"
                    >
                        {filterOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Pipeline state filter chips */}
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Pipeline State
                    </label>
                    <StateFilterChips
                        stateCounts={stateCounts}
                        selectedStates={filters.processingStates}
                        onChange={(states) => setFilters({ ...filters, processingStates: states })}
                    />
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={filteredTenders}
                loading={loading}
                error={error}
                emptyMessage="No tenders found"
            />

            {/* Create Modal */}
            <ModalForm
                isOpen={showCreateModal}
                onClose={() => {
                    setShowCreateModal(false);
                    resetForm();
                }}
                onSubmit={handleCreateTender}
                title="Create New Tender"
                submitLabel="Create Tender"
                isLoading={submitLoading}
                size="lg"
            >
                <TenderForm formData={formData} setFormData={setFormData} />
            </ModalForm>

            {/* Edit Modal */}
            <ModalForm
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setSelectedTender(null);
                    setSelectedAssessment(null);
                    resetForm();
                }}
                onSubmit={handleUpdateTender}
                title="Edit Tender"
                submitLabel="Save Changes"
                isLoading={submitLoading}
                size="lg"
            >
                <TenderForm formData={formData} setFormData={setFormData} isEdit />
            </ModalForm>

            {/* Detail Modal */}
            <Modal
                isOpen={showDetailModal}
                onClose={handleCloseDetailModal}
                title="Tender Details"
                size="xl"
            >
                {selectedTender && (
                    <>
                        <TenderDetailPanel
                            tender={selectedTender}
                            assessment={selectedAssessment}
                        />
                        <TenderArtifactsSection tenderId={selectedTender.id} />
                    </>
                )}
            </Modal>

            {/* Crawl Confirmation Modal */}
            <ModalConfirm
                isOpen={showCrawlModal}
                onClose={() => {
                    setShowCrawlModal(false);
                    setSelectedTender(null);
                }}
                onConfirm={handleCrawlTender}
                title="Start Crawl Job"
                message={`Are you sure you want to crawl tender "${selectedTender?.title}"? This will fetch all documents and details from the source.`}
                confirmLabel="Start Crawl"
                type="info"
            />

            {/* Assess Confirmation Modal */}
            <ModalConfirm
                isOpen={showAssessModal}
                onClose={() => {
                    setShowAssessModal(false);
                    setSelectedTender(null);
                }}
                onConfirm={handleAssessTender}
                title="Start Assessment"
                message={`Are you sure you want to assess tender "${selectedTender?.title}" for company "${companies.find(c => c.id === selectedCompanyId)?.name || 'Unknown'}"? This will analyze the tender using the selected company's fingerprint.`}
                confirmLabel="Start Assessment"
                type="info"
            />

            {/* Convert Documents Confirmation Modal */}
            <ModalConfirm
                isOpen={showConvertModal}
                onClose={() => {
                    setShowConvertModal(false);
                    setSelectedTender(null);
                }}
                onConfirm={handleConvertDocuments}
                title="Convert Documents"
                message={`Are you sure you want to convert documents for tender "${selectedTender?.title}"? This will extract text from all tender documents for processing.`}
                confirmLabel="Convert Documents"
                type="info"
            />

            {/* Deep Crawl Confirmation Modal */}
            <ModalConfirm
                isOpen={showDeepCrawlModal}
                onClose={() => {
                    setShowDeepCrawlModal(false);
                    setSelectedTender(null);
                }}
                onConfirm={handleDeepCrawl}
                title="Start Deep Crawl"
                message={`Are you sure you want to deep crawl tender "${selectedTender?.title}"? This will use LLM-powered analysis to extract additional documents from the tender page.`}
                confirmLabel="Start Deep Crawl"
                type="info"
            />

            {/* Processing State Modal */}
            <ProcessingStateModal
                isOpen={showStateModal}
                onClose={() => {
                    setShowStateModal(false);
                    setSelectedTender(null);
                }}
                tender={selectedTender}
                onAction={handleStateAction}
            />
        </div>
    );
};

// Tender Detail Panel Component
const TenderDetailPanel = ({ tender, assessment }) => {
    if (!tender) return null;

    const formatCurrency = (value, currency) => {
        if (!value) return '-';
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency || 'EUR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-green-600';
        if (score >= 60) return 'text-yellow-600';
        if (score >= 40) return 'text-orange-600';
        return 'text-red-600';
    };

    const getStrengthColor = (strength) => {
        switch (strength?.toLowerCase()) {
            case 'strong': return 'bg-green-100 text-green-700';
            case 'moderate': return 'bg-yellow-100 text-yellow-700';
            case 'weak': return 'bg-red-100 text-red-700';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    return (
        <div className="space-y-6">
            {/* Basic Info */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Basic Information</h4>
                <div className="surface-card p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <span className="text-gray-400">External ID:</span>
                            <span className="text-white ml-2">{tender.externalId}</span>
                        </div>
                        <div>
                            <span className="text-gray-400">Status:</span>
                            <span className="ml-2">
                                <StatusBadge status={tender.status} />
                            </span>
                        </div>
                    </div>

                    {tender.description && (
                        <div>
                            <span className="text-gray-400">Description:</span>
                            <p className="text-white mt-1 text-sm">{tender.description}</p>
                        </div>
                    )}

                    <div className="grid grid-cols-3 gap-4 pt-2">
                        {tender.type && (
                            <div>
                                <span className="text-gray-400 text-sm">Type:</span>
                                <div className="text-white">{tender.type}</div>
                            </div>
                        )}
                        {tender.procedureType && (
                            <div>
                                <span className="text-gray-400 text-sm">Procedure:</span>
                                <div className="text-white">{tender.procedureType}</div>
                            </div>
                        )}
                        {tender.rootCpv && (
                            <div>
                                <span className="text-gray-400 text-sm">CPV Code:</span>
                                <div className="text-white font-mono text-sm">{tender.rootCpv}</div>
                            </div>
                        )}
                    </div>

                    {/* Estimated Value & Flags */}
                    <div className="grid grid-cols-3 gap-4 pt-2 border-t border-gray-600">
                        {tender.estimatedValue && (
                            <div>
                                <span className="text-gray-400 text-sm">Estimated Value:</span>
                                <div className="text-white text-lg font-medium">
                                    {formatCurrency(tender.estimatedValue, tender.currency)}
                                </div>
                            </div>
                        )}
                        <div className="flex items-center space-x-4">
                            {tender.isFramework && (
                                <span className="px-2 py-1 rounded-lg bg-blue-100 text-blue-700 text-xs">Framework</span>
                            )}
                            {tender.hasOptions && (
                                <span className="px-2 py-1 rounded-lg bg-purple-100 text-purple-700 text-xs">Has Options</span>
                            )}
                        </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-4 gap-4 pt-2 border-t border-gray-600">
                        {tender.publicationDate && (
                            <div>
                                <span className="text-gray-400 text-sm">Published:</span>
                                <div className="text-white text-sm">
                                    {new Date(tender.publicationDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}
                        {tender.startDate && (
                            <div>
                                <span className="text-gray-400 text-sm">Start Date:</span>
                                <div className="text-white text-sm">
                                    {new Date(tender.startDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}
                        {tender.deadline && (
                            <div>
                                <span className="text-gray-400 text-sm">Deadline:</span>
                                <div className="text-white text-sm">
                                    {new Date(tender.deadline).toLocaleString()}
                                </div>
                            </div>
                        )}
                        {tender.endDate && (
                            <div>
                                <span className="text-gray-400 text-sm">End Date:</span>
                                <div className="text-white text-sm">
                                    {new Date(tender.endDate).toLocaleDateString()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Location */}
                    {tender.location && (
                        <div className="pt-2 border-t border-gray-600">
                            <span className="text-gray-400 text-sm">Location:</span>
                            <div className="text-white text-sm">
                                {[
                                    tender.location.address,
                                    tender.location.city,
                                    tender.location.state,
                                    tender.location.postalCode,
                                    tender.location.country,
                                    tender.location.nutsCode ? `(${tender.location.nutsCode})` : null
                                ].filter(Boolean).join(', ')}
                            </div>
                        </div>
                    )}

                    {tender.url && (
                        <div className="pt-2 border-t border-gray-600">
                            <span className="text-gray-400">URL:</span>
                            <a
                                href={tender.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-blue-700 ml-2 text-sm"
                            >
                                {tender.url}
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {/* Processing Status */}
            <div>
                <h4 className="text-sm font-medium text-gray-300 mb-2">Processing Status</h4>

                {/* Pipeline Timeline */}
                <div className="surface-card p-4 mb-4">
                    <ProcessingStateTimeline
                        currentState={tender.processingState || 'DRAFT'}
                        stateChangedAt={tender.stateChangedAt}
                        showEnrichmentBranch={['ENRICHING', 'ENRICHED'].includes(tender.processingState)}
                    />
                </div>

                <div className="grid grid-cols-3 gap-4">
                    {/* Pipeline State */}
                    <div className="surface-card p-4">
                        <h5 className="text-sm font-medium text-white mb-2">Pipeline State</h5>
                        <div className="space-y-2 text-sm">
                            <div>
                                <StatusBadge status={tender.processingState} />
                            </div>
                            {tender.stateChangedAt && (
                                <div>
                                    <span className="text-gray-400">State Changed:</span>
                                    <span className="text-white ml-2">
                                        {new Date(tender.stateChangedAt).toLocaleString()}
                                    </span>
                                </div>
                            )}
                            {tender.processingState === 'ERROR' && (
                                <div className="text-red-700 text-xs">
                                    Tender is in error state - may need manual intervention
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Crawl Status */}
                    <div className="surface-card p-4">
                        <h5 className="text-sm font-medium text-white mb-2">Crawl</h5>
                        <div className="space-y-2 text-sm">
                            <div>
                                <StatusBadge status={tender.crawlStatus} />
                            </div>
                            <div>
                                <span className="text-gray-400">Last Crawled:</span>
                                <span className="text-white ml-2">
                                    {tender.lastCrawled ? new Date(tender.lastCrawled).toLocaleString() : 'Never'}
                                </span>
                            </div>
                            {tender.crawlError && (
                                <div className="text-red-700 text-xs">
                                    Error: {tender.crawlError}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Assessment Status */}
                    <div className="surface-card p-4">
                        <h5 className="text-sm font-medium text-white mb-2">Assessment</h5>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                <StatusBadge status={assessment ? 'COMPLETED' : 'NONE'} />
                                {assessment?.isOutdated && (
                                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700">
                                        Outdated
                                    </span>
                                )}
                            </div>
                            {assessment?.error && (
                                <div className="text-red-700 text-xs">
                                    Error: {assessment.error}
                                </div>
                            )}
                            {!assessment && (
                                <div className="text-gray-400 text-xs">
                                    No assessment available
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Assessment Results - New Schema */}
            {assessment && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Assessment Results</h4>
                    {assessment.isOutdated && (
                        <div className="mb-3 flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <span className="text-yellow-700 font-medium">Fingerprint changed</span>
                                <p className="text-yellow-700/80 mt-0.5">
                                    This assessment was created with a different company fingerprint. Consider re-running the assessment for updated results.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="surface-card p-4 space-y-4">
                        {/* Overall Score */}
                        <div className="flex items-center justify-between">
                            <div>
                                <span className="text-gray-400">Overall Score:</span>
                                <div className={`text-3xl font-bold ${getScoreColor(assessment.overallScore)}`}>
                                    {assessment.overallScore}/100
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-gray-400 text-sm">Pipeline:</span>
                                <div className="text-white">{assessment.pipeline}</div>
                                <span className="text-gray-400 text-sm">Processing Time:</span>
                                <div className="text-white">{assessment.processingTimeMs}ms</div>
                            </div>
                        </div>

                        {/* Criterion Scores */}
                        {assessment.criterionScores && assessment.criterionScores.length > 0 && (
                            <div className="border-t border-gray-600 pt-4">
                                <h5 className="text-sm font-medium text-white mb-3">Criterion Scores</h5>
                                <div className="grid grid-cols-2 gap-3">
                                    {assessment.criterionScores.map((criterion) => (
                                        <div key={criterion.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-300 text-sm">{criterion.label}</span>
                                                <span className={`font-bold ${getScoreColor(criterion.score)}`}>
                                                    {criterion.score}
                                                </span>
                                            </div>
                                            <div className="mt-2 h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full ${criterion.score >= 60 ? 'bg-green-500' : criterion.score >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                                    style={{ width: `${criterion.score}%` }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Match Reasons */}
                        {assessment.matchReasons && assessment.matchReasons.length > 0 && (
                            <div className="border-t border-gray-600 pt-4">
                                <h5 className="text-sm font-medium text-white mb-3">Match Reasons</h5>
                                <div className="space-y-3">
                                    {assessment.matchReasons.map((reason) => (
                                        <div key={reason.id} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-white font-medium">{reason.label}</span>
                                                <span className={`px-2 py-1 rounded-lg text-xs ${getStrengthColor(reason.strength)}`}>
                                                    {reason.strength}
                                                </span>
                                            </div>
                                            {reason.evidence && reason.evidence.length > 0 && (
                                                <div className="mt-2 space-y-2">
                                                    {reason.evidence.slice(0, 3).map((ev, idx) => (
                                                        <div key={idx} className="text-xs bg-white border border-slate-200 rounded-lg p-2">
                                                            <div className="flex justify-between text-gray-400 mb-1">
                                                                <span>{ev.documentTitle}</span>
                                                            </div>
                                                            <p className="text-gray-300 italic">&ldquo;{ev.snippet}&rdquo;</p>
                                                        </div>
                                                    ))}
                                                    {reason.evidence.length > 3 && (
                                                        <div className="text-xs text-gray-500">
                                                            +{reason.evidence.length - 3} more evidence items
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Assessment Metadata */}
                        <div className="border-t border-gray-600 pt-4 text-sm text-gray-400">
                            <div className="flex justify-between">
                                <span>Assessed: {new Date(assessment.assessedAt).toLocaleString()}</span>
                                <span>Updated: {new Date(assessment.updatedAt).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Documents */}
            {tender.documents && tender.documents.length > 0 && (
                <div>
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Documents ({tender.documents.length})</h4>
                    <div className="surface-card p-4">
                        <div className="space-y-2">
                            {tender.documents.map((doc) => (
                                <div key={doc.id} className="flex justify-between items-center text-sm">
                                    <div className="flex items-center">
                                        <DocumentTextIcon className="h-4 w-4 text-gray-400 mr-2" />
                                        <span className="text-white">{doc.title}</span>
                                        <span className="text-gray-400 ml-2">({doc.fileType})</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-gray-400">{(doc.fileSize / 1024).toFixed(1)}KB</span>
                                        <StatusBadge status={doc.isIndexed ? 'indexed' : 'pending'} size="xs" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Tender Form Component
// AI artifacts (narrative / facet analysis) and export bundles for one tender.
// All three loaders are lazy — nothing is fetched until the admin asks.
// Narrative/facet queries are feature-gated on the caller's company
// (narrative_assessment / facet_analysis); a disabled feature is shown as a
// hint instead of an error (self-grant via the Features page).
const TenderArtifactsSection = ({ tenderId }) => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const [language, setLanguage] = useState('de');
    const [narrative, setNarrative] = useState({ state: 'idle', data: null, error: null });
    const [facets, setFacets] = useState({ state: 'idle', data: null, error: null });
    const [download, setDownload] = useState({ state: 'idle', data: null, error: null });
    const [flavor, setFlavor] = useState('CONVERTED');
    const [includeBidlyzerDocs, setIncludeBidlyzerDocs] = useState(true);
    const [jobFeedback, setJobFeedback] = useState(null);
    const pollRef = useRef(null);

    // Stop polling when the modal unmounts or the tender changes
    useEffect(() => {
        setNarrative({ state: 'idle', data: null, error: null });
        setFacets({ state: 'idle', data: null, error: null });
        setDownload({ state: 'idle', data: null, error: null });
        setJobFeedback(null);
        return () => clearTimeout(pollRef.current);
    }, [tenderId]);

    const featureHint = (err) =>
        /feature/i.test(err?.message || '')
            ? 'Feature disabled for your admin company — grant it on the Features page.'
            : err.message;

    const loadNarrative = async (lang = language) => {
        setNarrative({ state: 'loading', data: null, error: null });
        try {
            const data = await executeQuery(queries.getTenderNarrativeAssessment(), { tenderId, language: lang });
            setNarrative({ state: 'loaded', data: data.tenderNarrativeAssessment, error: null });
        } catch (err) {
            setNarrative({ state: 'error', data: null, error: featureHint(err) });
        }
    };

    const loadFacets = async (lang = language) => {
        setFacets({ state: 'loading', data: null, error: null });
        try {
            const data = await executeQuery(queries.getTenderFacetAnalysis(), { tenderId, language: lang });
            setFacets({ state: 'loaded', data: data.tenderFacetAnalysis, error: null });
        } catch (err) {
            setFacets({ state: 'error', data: null, error: featureHint(err) });
        }
    };

    const enqueueGeneration = async (type, regenerate) => {
        setJobFeedback(null);
        try {
            const result = await executeMutation(mutations.adminEnqueueJob, {
                type,
                tenderId,
                parameters: { language, ...(regenerate && { forceRegenerate: true }) },
            });
            const r = result.adminEnqueueJob;
            setJobFeedback(r?.success
                ? { ok: true, text: `${type} job enqueued (${r.jobId || 'no id'})` }
                : { ok: false, text: r?.error || 'Failed to enqueue job' });
        } catch (err) {
            setJobFeedback({ ok: false, text: err.message });
        }
    };

    const pollDownload = async (id, attempt = 0) => {
        try {
            const data = await executeQuery(queries.getTenderDownload(), { id });
            const dl = data.tenderDownload;
            if (!dl) {
                setDownload({ state: 'error', data: null, error: 'Download not found (expired or foreign id)' });
                return;
            }
            if (dl.status === 'READY' || dl.status === 'FAILED' || dl.status === 'EXPIRED') {
                setDownload({ state: 'loaded', data: dl, error: dl.status === 'FAILED' ? dl.error : null });
                return;
            }
            if (attempt >= 30) {
                setDownload({ state: 'error', data: dl, error: 'Still building after 60s — check back via job list' });
                return;
            }
            setDownload({ state: 'polling', data: dl, error: null });
            pollRef.current = setTimeout(() => pollDownload(id, attempt + 1), 2000);
        } catch (err) {
            setDownload({ state: 'error', data: null, error: err.message });
        }
    };

    const requestBundle = async () => {
        setDownload({ state: 'polling', data: null, error: null });
        try {
            const result = await executeMutation(mutations.requestTenderDownload, {
                input: { tenderId, flavor, includeBidlyzerDocs },
            });
            const dl = result.requestTenderDownload;
            pollDownload(dl.id);
        } catch (err) {
            setDownload({ state: 'error', data: null, error: featureHint(err) });
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const mb = bytes / (1024 * 1024);
        return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
    };

    const severityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'high': return 'bg-red-100 text-red-700';
            case 'medium': return 'bg-yellow-100 text-yellow-700';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const sectionBtn = 'px-2 py-1 rounded-lg font-mono text-xs font-bold uppercase bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-150';

    return (
        <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-300">AI Artifacts &amp; Export</h4>
                <div className="flex gap-0 border border-gray-600">
                    {['de', 'en'].map(lang => (
                        <button
                            key={lang}
                            type="button"
                            onClick={() => {
                                setLanguage(lang);
                                if (narrative.state !== 'idle') loadNarrative(lang);
                                if (facets.state !== 'idle') loadFacets(lang);
                            }}
                            className={`px-2 py-0.5 font-mono text-xs font-bold uppercase ${language === lang ? 'bg-primary text-on-accent' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
                        >
                            {lang}
                        </button>
                    ))}
                </div>
            </div>

            {jobFeedback && (
                <div className={`p-2 text-xs font-mono border ${jobFeedback.ok ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                    {jobFeedback.text}
                </div>
            )}

            {/* Narrative */}
            <div className="surface-card p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs font-bold text-gray-300 uppercase">Narrative Assessment</span>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => loadNarrative()} className={sectionBtn}>
                            {narrative.state === 'loading' ? 'Loading…' : 'Load'}
                        </button>
                        <button
                            type="button"
                            onClick={() => enqueueGeneration('generate_narrative_assessment', !!narrative.data)}
                            className={sectionBtn}
                            title="Enqueue a generate_narrative_assessment job for this tender"
                        >
                            {narrative.data ? 'Regenerate' : 'Generate'}
                        </button>
                    </div>
                </div>
                {narrative.state === 'error' && <p className="text-xs text-yellow-700">{narrative.error}</p>}
                {narrative.state === 'loaded' && !narrative.data && (
                    <p className="text-xs text-gray-400">No narrative for this tender/language yet — the nightly bulk job fills gaps, or generate one now.</p>
                )}
                {narrative.data && (
                    <div className="space-y-3 text-sm">
                        <p className="text-white">{narrative.data.summary}</p>
                        <div className="flex flex-wrap gap-1">
                            <span className="px-2 py-0.5 rounded-lg bg-purple-100 text-purple-700 text-xs">{narrative.data.domainPrimary}</span>
                            {narrative.data.isFramework && <span className="px-2 py-0.5 rounded-lg bg-blue-100 text-blue-700 text-xs">Framework</span>}
                            {narrative.data.requiresLocalPresence && <span className="px-2 py-0.5 rounded-lg bg-teal-100 text-teal-700 text-xs">Local presence</span>}
                            {narrative.data.contractForm && <span className="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-xs">{narrative.data.contractForm}</span>}
                            {narrative.data.taxonomyTags?.slice(0, 8).map(tag => (
                                <span key={tag} className="px-2 py-0.5 rounded-lg bg-slate-100 text-slate-600 text-xs border border-slate-200">{tag}</span>
                            ))}
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-xs">
                            <div>
                                <span className="text-gray-400">Budget estimate:</span>
                                <div className="text-white">{narrative.data.budgetEstimateEUR ? `€${Math.round(narrative.data.budgetEstimateEUR).toLocaleString('de-DE')}` : 'not stated'}</div>
                            </div>
                            <div>
                                <span className="text-gray-400">Duration:</span>
                                <div className="text-white">{narrative.data.durationMonths ? `${narrative.data.durationMonths} months` : 'not stated'}</div>
                            </div>
                            <div>
                                <span className="text-gray-400">Skills:</span>
                                <div className="text-white truncate" title={narrative.data.requiredSkills?.join(', ')}>{narrative.data.requiredSkills?.slice(0, 4).join(', ') || '-'}</div>
                            </div>
                        </div>
                        {narrative.data.risks?.length > 0 && (
                            <div>
                                <span className="text-gray-400 text-xs">Risks:</span>
                                <ul className="mt-1 space-y-1">
                                    {narrative.data.risks.map((risk, i) => (
                                        <li key={i} className="text-xs text-gray-200 flex items-start gap-2">
                                            <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase shrink-0 ${severityColor(risk.severity)}`}>{risk.severity}</span>
                                            {risk.description}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        <p className="text-[10px] text-gray-500 font-mono">
                            {narrative.data.model} · {narrative.data.promptVersion} · {narrative.data.inputTokens}/{narrative.data.outputTokens} tok · {narrative.data.processingTimeMs} ms · {new Date(narrative.data.generatedAt).toLocaleString()}
                        </p>
                    </div>
                )}
            </div>

            {/* Facet analysis */}
            <div className="surface-card p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs font-bold text-gray-300 uppercase">Facet Analysis</span>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => loadFacets()} className={sectionBtn}>
                            {facets.state === 'loading' ? 'Loading…' : 'Load'}
                        </button>
                        <button
                            type="button"
                            onClick={() => enqueueGeneration('generate_facet_analysis', !!facets.data)}
                            className={sectionBtn}
                            title="Enqueue a generate_facet_analysis job for this tender"
                        >
                            {facets.data ? 'Regenerate' : 'Generate'}
                        </button>
                    </div>
                </div>
                {facets.state === 'error' && <p className="text-xs text-yellow-700">{facets.error}</p>}
                {facets.state === 'loaded' && !facets.data && (
                    <p className="text-xs text-gray-400">No facet analysis for this tender/language yet.</p>
                )}
                {facets.data && (
                    <div className="space-y-2">
                        {facets.data.facets.map(facet => (
                            <div key={facet.key} className="border-b border-gray-600 pb-2 last:border-b-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-medium text-white">{facet.label}</span>
                                    {!facet.hasInfo && <span className="text-[10px] text-gray-500 uppercase">not specified</span>}
                                </div>
                                {facet.hasInfo && (
                                    <>
                                        <p className="text-xs text-gray-200 mt-1">{facet.answer}</p>
                                        {facet.keyPoints?.length > 0 && (
                                            <ul className="mt-1 ml-4 list-disc text-xs text-gray-300">
                                                {facet.keyPoints.map((kp, i) => <li key={i}>{kp}</li>)}
                                            </ul>
                                        )}
                                    </>
                                )}
                            </div>
                        ))}
                        <p className="text-[10px] text-gray-500 font-mono">{facets.data.model} · {facets.data.promptVersion}</p>
                    </div>
                )}
            </div>

            {/* Export bundle */}
            <div className="surface-card p-4">
                <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs font-bold text-gray-300 uppercase">Export Bundle (ZIP)</span>
                    <span className="text-[10px] text-gray-500 uppercase">Watermarked &amp; traceable per export</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <select
                        value={flavor}
                        onChange={(e) => setFlavor(e.target.value)}
                        className="bg-gray-800 border border-gray-600 text-white p-1.5 text-xs"
                    >
                        <option value="CONVERTED">Converted (uniform PDFs)</option>
                        <option value="ORIGINALS">Originals (bit-identical)</option>
                    </select>
                    <label className="flex items-center gap-1.5 text-xs text-gray-300">
                        <input
                            type="checkbox"
                            checked={includeBidlyzerDocs}
                            onChange={(e) => setIncludeBidlyzerDocs(e.target.checked)}
                        />
                        + BidLyzer documents
                    </label>
                    <button
                        type="button"
                        onClick={requestBundle}
                        disabled={download.state === 'polling'}
                        className={`${sectionBtn} disabled:opacity-50`}
                    >
                        {download.state === 'polling' ? 'Building…' : 'Request Bundle'}
                    </button>
                </div>
                {download.error && <p className="text-xs text-red-700 mt-2">{download.error}</p>}
                {download.data?.status === 'READY' && (
                    <div className="mt-2 flex items-center gap-3 text-xs">
                        <a
                            href={download.data.url}
                            className="px-2 py-1 bg-primary text-on-accent font-mono font-bold uppercase hover:brightness-110"
                        >
                            Download
                        </a>
                        <span className="text-gray-300">
                            {download.data.fileCount} files · {formatBytes(download.data.bundleBytes)}
                            {download.data.missingCount > 0 && <span className="text-yellow-700"> · {download.data.missingCount} missing (see manifest)</span>}
                        </span>
                        <span className="text-gray-500">link valid 15 min — re-request for a fresh one</span>
                    </div>
                )}
                {download.data?.status === 'EXPIRED' && (
                    <p className="text-xs text-gray-400 mt-2">Bundle expired (7-day retention) — request again to rebuild.</p>
                )}
            </div>
        </div>
    );
};

const TenderForm = ({ formData, setFormData, isEdit = false }) => {
    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Title *</label>
                <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    required
                />
            </div>

            {!isEdit && (
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">External ID *</label>
                    <input
                        type="text"
                        value={formData.externalId}
                        onChange={(e) => setFormData(prev => ({ ...prev, externalId: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        required
                        placeholder="Unique identifier from source system"
                    />
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    rows="4"
                    placeholder="Detailed description of the tender"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Type</label>
                    <input
                        type="text"
                        value={formData.type}
                        onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                        placeholder="e.g., IT Services, Construction"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Location</label>
                    <div className="space-y-2">
                        <input
                            type="text"
                            value={formData.location.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, address: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Address"
                        />
                        <input
                            type="text"
                            value={formData.location.city}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, city: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="City"
                        />
                        <input
                            type="text"
                            value={formData.location.state}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, state: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="State"
                        />
                        <input
                            type="text"
                            value={formData.location.postalCode}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, postalCode: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Postal Code"
                        />
                        <input
                            type="text"
                            value={formData.location.country}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, country: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="Country"
                        />
                        <input
                            type="text"
                            value={formData.location.nutsCode}
                            onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, nutsCode: e.target.value } }))}
                            className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                            placeholder="NUTS Code"
                        />
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                type="number"
                                step="0.000001"
                                value={formData.location.latitude || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, latitude: e.target.value ? parseFloat(e.target.value) : null } }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                placeholder="Latitude"
                            />
                            <input
                                type="number"
                                step="0.000001"
                                value={formData.location.longitude || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, location: { ...prev.location, longitude: e.target.value ? parseFloat(e.target.value) : null } }))}
                                className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                                placeholder="Longitude"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">URL</label>
                <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    placeholder="https://example.com/tender-123"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Start Date</label>
                    <input
                        type="date"
                        value={formData.startDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Deadline</label>
                    <input
                        type="datetime-local"
                        value={formData.deadline}
                        onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                        className="w-full bg-gray-700 border border-gray-600 text-white p-2"
                    />
                </div>
            </div>
        </div>
    );
};

export default Tenders;